<template>
  <div class="layout -admin">
    <HoAdminHeader class="header">
      <template #title>
        <slot name="header-title" />
      </template>
      <template #user>
        <slot name="header-user" />
      </template>
      <template #logout>
        <slot name="header-logout" />
      </template>
    </HoAdminHeader>
    <div class="body">
      <HoAdminSideMenu
        v-if="sections.length > 0"
        :items="sections"
        class="sidebar"
      />
      <div
        v-else-if="!built"
        class="sidebar sidebar-skeleton"
        aria-hidden="true"
      >
        <div
          v-for="s in 4"
          :key="s"
          class="skeleton-section"
        >
          <div class="skeleton-bar skeleton-label" />
          <div
            v-for="i in (s === 1 ? 3 : s === 2 ? 4 : 2)"
            :key="i"
            class="skeleton-bar skeleton-item"
          />
        </div>
      </div>
      <main class="main">
        <div
          v-if="breadcrumbs.length > 0"
          class="breadcrumb-bar"
        >
          <HoAdminBreadcrumb :items="breadcrumbs" />
        </div>
        <div class="content">
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAdminSideMenu } from '#main/app/composables/useAdminSideMenu'
import { useAdminBreadcrumbs } from '#main/app/composables/useAdminBreadcrumbs'

const { sections, built } = useAdminSideMenu()
const { breadcrumbs } = useAdminBreadcrumbs()
</script>

<style lang="scss">
/* ─────────────────────────────────────────────────────────────────────────
   管理画面フィルタフォーム カード外観
   .search-form クラスを持つ要素に適用。HmSearchForm (.hm-search-form) は別クラスのため対象外。
   ─────────────────────────────────────────────────────────────────────── */
.layout.-admin .content .search-form {
  padding: 16px;
  border: 1px solid #dee2e6;
  border-radius: 6px;

  background: #fff;
  box-shadow: 0 1px 3px rgb(0 0 0 / 6%);
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-bar { animation: none !important; }
}
</style>

<style lang="scss" scoped>
@use '#base/app/assets/styles/variables' as v;

.layout.-admin {
  display: flex;
  flex-direction: column;

  min-width: v.$pc-content-min-width;
  min-height: 100vh;

  background-color: v.$admin-surface-2;

  > .header {
    position: sticky;
    z-index: v.$zindex-header;
    top: 0;
  }

  > .body {
    display: flex;
    flex: 1;
    height: calc(100vh - v.$admin-header-height);

    > .sidebar {
      position: sticky;
      top: v.$admin-header-height;

      flex-shrink: 0;
      align-self: flex-start;

      height: calc(100vh - v.$admin-header-height);
    }

    > .sidebar-skeleton {
      overflow: hidden;
      width: v.$admin-sidebar-width;
      min-width: v.$admin-sidebar-width;
      border-right: 1px solid v.$admin-border-color;
      background-color: v.$admin-surface-2;
      padding: 12px 0;

      .skeleton-section {
        padding: 4px 0 8px;
      }

      .skeleton-bar {
        border-radius: 3px;
        background: linear-gradient(90deg, v.$admin-surface-3 25%, v.$admin-border-color 50%, v.$admin-surface-3 75%);
        background-size: 200% 100%;
        animation: skeleton-shimmer 1.4s ease-in-out infinite;
      }

      .skeleton-label {
        height: 10px;
        width: 100px;
        margin: 4px 16px 6px;
      }

      .skeleton-item {
        height: 9px;
        margin: 6px 16px 6px 28px;

        &:nth-child(2) { width: 120px; }
        &:nth-child(3) { width: 100px; }
        &:nth-child(4) { width: 90px; }
        &:nth-child(5) { width: 110px; }
      }
    }

    > .main {
      overflow-y: auto;
      flex: 1;
      min-width: 0;

      > .breadcrumb-bar {
        padding: 12px 24px 0;
      }

      > .content {
        padding: 16px 24px 48px;

        // グローバル既定の $base-font-color は暗色テーマ向けの白なので、
        // 明色サーフェスの管理画面コンテンツでは本文色を上書きしておく
        color: v.$admin-text-primary;
      }
    }
  }
}
</style>
